import {useCallback, useEffect, useState} from 'react'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import sendCode from '../api/sendCode'
import {useUser} from 'features/authorized/context/user'
import {UserData} from 'features/common/core/entitites'
import checkCode from '../api/checkCode'

const defaultTimeout = 60
const codeLength = 6

const useEmailCode = () => {
    const qc = useQueryClient()
    const {user} = useUser() as {user: UserData}
    const [resendTimeout, setResendTimeout] = useState(0)
    const [isResendTimeout, setIsResendTimeout] = useState(false)
    const [codeInputValue, setCodeInputValue] = useState('')

    const {mutate: sendMutation, isLoading: isSending} = useMutation(sendCode, {
        onSuccess: (data) => {
            setResendTimeout(data.resendTimeout)
            setIsResendTimeout(true)
            setCodeInputValue('')
        },
        onError: () => {
            if (resendTimeout > 0) {
                return
            }
            setResendTimeout(defaultTimeout)
            setIsResendTimeout(true)
        },
    })
    const {mutate: checkMutation, error: checkError} = useMutation(checkCode, {
        onSuccess: () => {
            void qc.invalidateQueries(['user'])
        },
    })

    useEffect(() => {
        if (!isResendTimeout) {
            return
        }
        const interval = setInterval(() => {
            setResendTimeout((rt) => {
                if (rt <= 0) {
                    clearInterval(interval)
                    setIsResendTimeout(false)
                    return 0
                }
                return rt - 1
            })
        }, 1000)
        return () => {
            clearInterval(interval)
        }
    }, [isResendTimeout])

    useEffect(() => sendMutation(user.email), [sendMutation, user.email])

    const send = () => {
        if (isSending) {
            return
        }
        sendMutation(user.email)
    }

    const check = useCallback(
        (code: string) => checkMutation({email: user.email, code}),
        [checkMutation, user.email],
    )

    const onChangeCode = (code: string) => {
        setCodeInputValue(code)
    }

    return {
        isSending,
        email: user.email,
        resendTimeout,

        codeInputValue,
        codeLength,
        onChangeCode,

        checkError,

        send,
        check,
    }
}
export default useEmailCode
